import React, { useEffect, useId, useMemo, useRef } from 'react';
import { useAimCompetitorDetail } from '../lib/aimQueries';
import ProviderColorDot from '../primitives/ProviderColorDot';
import AimEmpty, { AimLoading } from '../primitives/AimEmpty';
import useFocusTrap from '../../../hooks/useFocusTrap';
import { prettifyCategory } from '../lib/categoryLabel';
import { prettifyProviderLabel } from '../lib/providerColors';
import { competitorDetailView } from '../lib/competitorDetail';
import { fmtFullRunDate } from '../lib/runLabel';
import WindowLabel from '../primitives/WindowLabel';
import { isPending } from '../lib/queryState';
import AimPagination from '../primitives/AimPagination';
import usePagedRows from '../lib/usePagedRows';
import { TONE_STYLES } from '../../shared/MetricCardShell';
import { TABLE_FRAME, TABLE_THEAD, TABLE_ROW } from '../../shared/tableStyles';

/**
 * One headline figure: a card outlined and valued in a tone from the shared
 * TONE_STYLES. Tones only tell the five apart, except Warnings (red) and
 * Superlative (green), which do carry meaning for a competitor.
 */
/** Outline per tone — the value's colour at 40%, so the card is framed in its own hue. */
const STAT_OUTLINES = {
  brandBlue: 'border-[#32B1FF]/40',
  purple: 'border-purple-500/40',
  amber: 'border-amber-500/40',
  emerald: 'border-emerald-500/40',
  red: 'border-red-500/40',
};

function Stat({ label, value, tone = 'brandBlue' }) {
  const style = TONE_STYLES[tone] ?? TONE_STYLES.brandBlue;
  return (
    <div className={`rounded-lg border ${STAT_OUTLINES[tone] ?? STAT_OUTLINES.brandBlue} px-3 py-2.5`} data-testid="competitor-stat" data-tone={tone}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-1 text-xl font-extrabold tabular-nums ${value == null ? 'text-gray-400 dark:text-gray-500' : style.valueClass}`}>
        {value == null ? '—' : value}
      </div>
    </div>
  );
}

/** Rows per page in the drawer's "Every mention" table. */
export const MENTIONS_PAGE_SIZE = 15;

/** Pill colours per recommendation strength; anything unrecognised is neutral. */
const STRENGTH_STYLES = {
  strong_recommend: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
  recommend: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  suggest: 'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300',
  mention: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  warning: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  warn: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400',
};
const NEUTRAL_PILL = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';

/** A recommendation-strength pill ("Strong Recommend", "Suggest", …). */
function StrengthPill({ value }) {
  if (!value) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  const key = String(value).toLowerCase();
  return (
    <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${STRENGTH_STYLES[key] ?? NEUTRAL_PILL}`}>
      {prettifyCategory(value)}
    </span>
  );
}

/** A list position as a compact rank badge (#1 reads strongest). */
function PositionBadge({ value }) {
  if (value == null) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  return (
    <span className="inline-flex min-w-[1.75rem] justify-center rounded-md bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs font-bold tabular-nums text-gray-800 dark:text-gray-100">
      {value}
    </span>
  );
}

/** A table caption styled as the section band used across the dashboard's drawers. */
const bandCaption = 'caption-top text-left bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800';

/**
 * How a competitor's mentions split across the window's runs, newest first, with the run the
 * page is on marked. A competitor with none in the selected run still shows the runs that have
 * some, which is what tells "absent now" from "never mentioned".
 */
function MentionsByRun({ byRun, selectedRunId }) {
  return (
    <section aria-label="Mentions by run" data-testid="competitor-by-run">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Mentions by run</h3>
      <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {byRun.map((r) => {
          const selected = r.run_id === selectedRunId;
          return (
            <li
              key={r.run_id}
              data-selected={selected ? 'true' : undefined}
              className={`rounded-lg border px-3 py-2 ${selected ? 'border-[#32B1FF]/60 bg-[#32B1FF]/5' : 'border-gray-200 dark:border-gray-800'}`}
            >
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                {fmtFullRunDate({ created_at: r.run_at }) || '—'}
                {selected && <span className="ml-1 font-semibold text-[#32B1FF]">· selected</span>}
              </div>
              <div className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">{r.mentions == null ? '—' : r.mentions}</div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function CompetitorDetailPanel({ clientId, runId, competitorName, aliases, tableMentions, onClose }) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const titleId = useId();
  useFocusTrap(dialogRef);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    closeBtnRef.current?.focus();
    return () => {
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        requestAnimationFrame(() => prev.focus());
      }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const detailQuery = useAimCompetitorDetail(clientId, runId, competitorName);
  const { data: payload } = detailQuery;
  // The hook is disabled until there is a run, and a disabled hook is not pending; with no run the
  // drawer is still waiting for one (the tab holds it), not empty.
  const loading = !runId || isPending(detailQuery);
  // Memoized on the payload: competitorDetailView maps into fresh arrays, and
  // usePagedRows resets to page 0 whenever its row array identity changes.
  const view = useMemo(() => competitorDetailView(payload), [payload]);
  const {
    page, setPage, pageCount, visibleRows: visibleMentions, totalItems,
  } = usePagedRows(view.mentions, MENTIONS_PAGE_SIZE);
  const aliasList = Array.isArray(aliases) ? aliases.filter(Boolean) : [];
  // What the merged table row counts beyond this drawer's single name: 0 (so unsaid) when either
  // figure is missing.
  const drawerTotal = view.summary?.total_mentions;
  const variantMentions = tableMentions != null && drawerTotal != null
    ? Math.max(0, Number(tableMentions) - drawerTotal)
    : 0;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-40 flex"
    >
      <button
        type="button"
        className="flex-1 bg-black/40 cursor-default"
        aria-label="Close panel"
        onClick={onClose}
      />
      <aside className="w-full sm:w-[920px] bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-800 overflow-y-auto">
        <header className="sticky top-0 z-10 px-5 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">
              {competitorName || 'Competitor'} — Detail
            </h2>
            {/* The same window label as the table the drawer was opened from. */}
            <div className="mt-0.5"><WindowLabel label={view.scope?.label} /></div>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 dark:hover:text-white text-sm"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="p-5 space-y-4">
          {loading ? (
            <AimLoading height={160} />
          ) : view.mode === 'empty' ? (
            <AimEmpty message="No mentions of this competitor in this window." />
          ) : (
            <>
              {view.summary && (
                <div data-testid="competitor-stats" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  <Stat label="Mentions" value={view.summary.total_mentions} tone="brandBlue" />
                  <Stat label="Providers" value={view.summary.providers_mentioning} tone="purple" />
                  <Stat
                    label="Avg position"
                    tone="amber"
                    value={view.summary.avg_position == null ? null : view.summary.avg_position.toFixed(1)}
                  />
                  <Stat label="Superlative" value={view.summary.superlative_mentions} tone="emerald" />
                  <Stat label="Warnings" value={view.summary.warning_mentions} tone="red" />
                </div>
              )}

              {view.providers.length > 0 && (
              <div className={`${TABLE_FRAME} responsive-card-table-wrapper`}>
                <table className="w-full text-sm text-gray-900 dark:text-gray-100 responsive-card-table">
                  <caption className={bandCaption}>
                    By provider <span className="font-normal normal-case text-gray-400">· {view.providers.length}</span>
                  </caption>
                  <thead className={TABLE_THEAD}>
                    <tr>
                      <th className="text-left px-3 py-2.5 whitespace-nowrap">Provider</th>
                      <th className="text-right px-3 py-2.5 whitespace-nowrap">Mentions</th>
                      <th className="text-right px-3 py-2.5 whitespace-nowrap">Avg position</th>
                      <th className="text-left px-3 py-2.5 whitespace-nowrap">Recommendation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.providers.map((r, i) => (
                      <tr key={`${r.provider}-${i}`} className={TABLE_ROW}>
                        <td className="px-3 py-3 font-medium" data-label="Provider">
                          <span className="inline-flex items-center gap-1.5">
                            <ProviderColorDot displayName={r.provider} />
                            <span>{prettifyProviderLabel(r.provider) || '—'}</span>
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums font-semibold" data-label="Mentions">
                          {r.mentions == null ? '—' : r.mentions}
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums" data-label="Avg position">
                          {r.avg_position == null ? '—' : r.avg_position.toFixed(1)}
                        </td>
                        <td className="px-3 py-3" data-label="Recommendation">
                          {r.strengths.length ? (
                            <span className="inline-flex flex-wrap gap-1" title={r.strengths.map(prettifyCategory).join(', ')}>
                              {r.strengths.map((st) => <StrengthPill key={st} value={st} />)}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}

              {view.byRun.length > 0 && <MentionsByRun byRun={view.byRun} selectedRunId={runId} />}

              {view.mentions.length > 0 && (
              <div className={`${TABLE_FRAME} responsive-card-table-wrapper`}>
              <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[640px] responsive-card-table">
              <caption className={bandCaption}>
                Every mention <span className="font-normal normal-case text-gray-400">· {totalItems}</span>
              </caption>
              <thead className={TABLE_THEAD}>
                <tr>
                  <th className="text-left px-3 py-2.5 whitespace-nowrap">Provider</th>
                  <th className="text-left px-3 py-2.5 whitespace-nowrap">Category</th>
                  <th className="text-left px-3 py-2.5 whitespace-nowrap">Prompt</th>
                  <th className="text-center px-3 py-2.5 whitespace-nowrap">Position</th>
                  <th className="text-left px-3 py-2.5 whitespace-nowrap">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {visibleMentions.map((r, i) => (
                  <tr key={i} className={TABLE_ROW}>
                    <td className="px-3 py-3 whitespace-nowrap" data-label="Provider">
                      <span className="inline-flex items-center gap-1.5">
                        <ProviderColorDot displayName={r.display_name} />
                        <span>{prettifyProviderLabel(r.display_name) || '—'}</span>
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap" data-label="Category">{r.category_display || prettifyCategory(r.category) || '—'}</td>
                    <td className="px-3 py-3 truncate max-w-[360px]" title={r.prompt_text} data-label="Prompt">{r.prompt_text || '—'}</td>
                    <td className="px-3 py-3 text-center" data-label="Position"><PositionBadge value={r.list_position} /></td>
                    <td className="px-3 py-3" data-label="Recommendation"><StrengthPill value={r.recommendation_strength} /></td>
                  </tr>
                ))}
              </tbody>
              </table>
              </div>
              )}
              {view.mentions.length > 0 && (
                <AimPagination
                  page={page}
                  pageCount={pageCount}
                  onPage={setPage}
                  totalItems={totalItems}
                  label="mention"
                />
              )}

              {/* The query merged name variants; the RPC does not, so a drawer
                  opened from a merged table row reports only its primary name
                  and will read lower than the row it came from. */}
              {aliasList.length > 0 && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Counts cover <strong>{competitorName}</strong> only. This competitor was merged
                  from {aliasList.length + 1} name variants in the
                  table ({aliasList.join(', ')}), whose mentions are not included here.
                  {variantMentions > 0 && (
                    <>
                      {' '}The table adds {variantMentions} mention{variantMentions === 1 ? '' : 's'} from name
                      variants: {tableMentions} in all.
                    </>
                  )}
                </p>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
